import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatIST } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, ChevronRight, RefreshCw, Package, MessageSquare, Flag } from 'lucide-react';

import {
  Screen, ScreenSplit, Pane, CommandBar, DataGrid, FactBand, Fact, CellButton,
} from '@/components/data';

import { STATUS_COLORS, PRIORITY_COLORS } from '@/lib/status-colors';

const QUALITY_ISSUE_TYPES = [
  { value: 'print_defect', label: 'Print Defect' },
  { value: 'material_damage', label: 'Material Damage' },
  { value: 'wrong_specification', label: 'Wrong Specification' },
  { value: 'other', label: 'Other' },
];

export default function SupervisorOrderDetailPage() {
  const { id: orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSupervisor = ['supervisor', 'admin'].includes(user?.role);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remarkText, setRemarkText] = useState('');
  const [addingRemark, setAddingRemark] = useState(false);
  const [priorityLoading, setPriorityLoading] = useState(false);

  // Quality flag
  const [qualityDialog, setQualityDialog] = useState(false);
  const [qualityItemId, setQualityItemId] = useState('');
  const [qualityType, setQualityType] = useState('print_defect');
  const [qualityNote, setQualityNote] = useState('');
  const [flagging, setFlagging] = useState(false);

  const fetchOrder = useCallback(async () => {
    if (!orderId) return;
    try {
      const res = await api.get(`/orders/${orderId}/details`);
      setOrder(res.data);
    } catch (e) {
      try {
        const res2 = await api.get(`/orders/${orderId}`);
        setOrder(res2.data);
      } catch (e2) {
        toast.error('Failed to load order');
      }
    }
    setLoading(false);
  }, [orderId]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const handlePriorityChange = async (newPriority) => {
    setPriorityLoading(true);
    try {
      await api.patch(`/orders/${orderId}/priority?priority=${newPriority}`);
      toast.success('Priority updated');
      fetchOrder();
    } catch (e) {
      toast.error('Failed to update priority');
    }
    setPriorityLoading(false);
  };

  const handleAddRemark = async () => {
    if (!remarkText.trim()) return;
    setAddingRemark(true);
    try {
      await api.put(`/orders/${orderId}/remarks`, { remark: remarkText.trim() });
      toast.success('Remark added');
      setRemarkText('');
      fetchOrder();
    } catch (e) {
      toast.error('Failed to add remark');
    }
    setAddingRemark(false);
  };

  const handleFlagQuality = async () => {
    setFlagging(true);
    try {
      await api.put(`/orders/${orderId}/flag-quality`, {
        item_id: qualityItemId,
        issue_type: qualityType,
        note: qualityNote,
      });
      toast.success('Quality issue flagged');
      setQualityDialog(false);
      setQualityNote('');
      fetchOrder();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to flag issue');
    }
    setFlagging(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-slate-700">Order Not Found</h2>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  const statusStyle = STATUS_COLORS[order.status] || STATUS_COLORS.in_production;
  const pColor = PRIORITY_COLORS[order.priority] || PRIORITY_COLORS.medium;
  const lineItems = order.line_items || [];
  const remarks = order.remarks || [];
  const qualityFlags = order.quality_flags || [];

  const cardColumns = [
    {
      key: 'item',
      header: 'Job card',
      width: 'minmax(13rem,1.5fr)',
      render: (item) => (
        <>
          <span className="block truncate font-semibold text-slate-900">
            {item.item_name || item.product_subtype || 'Item'}
          </span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">
            {item.order_type} · Qty {item.quantity?.toLocaleString()}
          </span>
        </>
      ),
    },
    {
      key: 'current_stage',
      header: 'Stage',
      width: '11rem',
      render: (item) => (
        <span className="block truncate text-[12px] font-medium text-brand-blue">
          {item.current_stage || (item.status === 'completed' ? 'Completed' : 'Not started')}
        </span>
      ),
    },
    {
      key: 'batches',
      header: 'Batches',
      width: '8rem',
      mobile: false,
      render: (item) => (
        <span className="text-[12px] text-slate-600">
          {item.batches?.length
            ? `${item.batches.length} batch${item.batches.length === 1 ? '' : 'es'}`
            : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '8rem',
      mobile: false,
      render: (item) => (
        <span className={`text-[12px] font-medium ${
          item.status === 'completed' ? 'text-emerald-600'
            : item.status === 'design' ? 'text-purple-600' : 'text-blue-600'
        }`}>
          {item.status?.replace('_', ' ') || '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '8rem',
      align: 'right',
      render: (item) => (
        <span className="flex items-center justify-end gap-0.5">
          <CellButton
            icon={Flag}
            label="Flag quality"
            tone="danger"
            onClick={() => { setQualityItemId(item.id); setQualityDialog(true); }}
          />
          <CellButton
            icon={ChevronRight}
            label="Open job card"
            iconOnly
            onClick={() => navigate(`/supervisor/job-card/${item.id}`)}
          />
        </span>
      ),
    },
  ];

  return (
    <Screen data-testid="supervisor-order-detail-page">
      <CommandBar
        title={order.order_id}
        subtitle={order.customer_name}
        onBack={() => navigate(-1)}
        badges={
          <>
            <Badge className={`text-[10px] ${statusStyle.class}`}>{statusStyle.label || order.status}</Badge>
            <Badge className={`text-[10px] ${pColor.class}`}>{order.priority}</Badge>
          </>
        }
        actions={[
          { key: 'flag', label: 'Flag Quality Issue', icon: Flag, tone: 'danger',
            onClick: () => { setQualityItemId(''); setQualityDialog(true); } },
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchOrder },
        ]}
      />

      {/* No money on this screen — a supervisor does not need the order value to
          run the floor, and leaving it out is simpler than gating it per field. */}
      <FactBand>
        <Fact label="Customer">{order.customer_name}</Fact>
        <Fact label="Firm">{order.firm_name}</Fact>
        <Fact label="Order date">{order.created_at ? formatIST(order.created_at, { dateOnly: true }) : '—'}</Fact>
        <Fact label="Delivery">{order.expected_delivery_date ? formatIST(order.expected_delivery_date, { dateOnly: true }) : '—'}</Fact>
        <Fact label="Job cards">{lineItems.length}</Fact>
        <Fact label="Quality flags" valueClassName={qualityFlags.length ? 'font-medium text-red-600' : undefined}>
          {qualityFlags.length}
        </Fact>
        <Fact label="Priority">
          <Select value={order.priority} onValueChange={handlePriorityChange}>
            <SelectTrigger className="h-5 border-0 px-0 text-xs shadow-none focus:ring-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['low', 'medium', 'high'].map(p => (
                <SelectItem key={p} value={p} className="text-xs capitalize">{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Fact>
        <Fact label="Status">{statusStyle.label || order.status}</Fact>
      </FactBand>

      <ScreenSplit
        railWidth="21rem"
        rail={
          <>
            {qualityFlags.length > 0 && (
              <Pane title="Quality issues" aside={`${qualityFlags.length}`} className="shrink-0">
                {qualityFlags.map((flag, i) => (
                  <div key={i} className="border-b border-slate-100 px-2.5 py-1.5 last:border-b-0">
                    <span className="block text-[12px] font-medium text-red-700">
                      {QUALITY_ISSUE_TYPES.find(t => t.value === flag.issue_type)?.label || flag.issue_type}
                    </span>
                    <span className="block text-[11px] leading-snug text-slate-600">{flag.description}</span>
                    <span className="block text-[10px] text-slate-400">
                      {flag.flagged_by_name}{flag.flagged_at ? ` · ${formatIST(flag.flagged_at, { compact: true })}` : ''}
                    </span>
                  </div>
                ))}
              </Pane>
            )}

            <Pane title="Special instructions" aside={`${remarks.length}`} className="min-h-0 flex-1">
              <div className="space-y-1.5 p-2">
                {Array.isArray(remarks) && remarks.map((r, i) => (
                  <p key={i} className="rounded bg-slate-50 px-2 py-1 text-[11px] text-slate-600">
                    {typeof r === 'string' ? r : r.text || JSON.stringify(r)}
                    {r.by && <span className="ml-1 text-slate-400">— {r.by}</span>}
                  </p>
                ))}
                {remarks.length === 0 && (
                  <p className="py-2 text-center text-[11px] text-slate-400">Nothing noted yet</p>
                )}
                <div className="flex gap-1.5 pt-1">
                  <Textarea
                    placeholder="Add a remark…"
                    value={remarkText}
                    onChange={(e) => setRemarkText(e.target.value)}
                    rows={2}
                    className="resize-none text-xs"
                  />
                  <Button
                    size="sm"
                    className="h-auto shrink-0"
                    disabled={!remarkText.trim() || addingRemark}
                    onClick={handleAddRemark}
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </Pane>
          </>
        }
      >
        <Pane
          title="Job cards"
          aside={`${lineItems.length}`}
          scroll={false}
          bodyClassName="flex flex-col"
        >
          <DataGrid
            data-testid="supervisor-order-cards"
            columns={cardColumns}
            rows={lineItems}
            getRowId={(item) => item.id}
            onRowClick={(item) => navigate(`/supervisor/job-card/${item.id}`)}
            empty="This order has no job cards"
          />
        </Pane>
      </ScreenSplit>

      <Dialog open={qualityDialog} onOpenChange={setQualityDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Flag Quality Issue</DialogTitle>
            <DialogDescription>Report a quality issue on this order</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Issue Type</label>
              <Select value={qualityType} onValueChange={setQualityType}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {QUALITY_ISSUE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Note (optional)</label>
              <Textarea
                value={qualityNote}
                onChange={(e) => setQualityNote(e.target.value)}
                placeholder="Describe the issue..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setQualityDialog(false)}>Cancel</Button>
            <Button onClick={handleFlagQuality} disabled={flagging} className="bg-red-600 hover:bg-red-700">
              {flagging ? 'Flagging...' : 'Flag Issue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Screen>
  );
}

